import { shuffle } from 'es-toolkit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import { seconds, SongPreview } from '~/interfaces';
import { useBackground } from '~/modules/elements/background-context';
import { NavButton } from '~/modules/elements/nav-controls';
import NoPrerender from '~/modules/elements/no-prerender';
import VideoPlayer, { VideoPlayerRef, VideoState } from '~/modules/elements/video-player/index';
import useBackgroundMusic from '~/modules/hooks/use-background-music';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import useSmoothNavigate, { buildUrl } from '~/modules/hooks/use-smooth-navigate';
import useViewportSize from '~/modules/hooks/use-viewport-size';
import { songGenre } from '~/modules/songs/hooks/use-genre-label';
import useSongIndex from '~/modules/songs/hooks/use-song-index';
import { formatSongLength } from '~/modules/songs/utils/format-song-length';
import groupDigits from '~/modules/utils/group-digits';
import isE2E from '~/modules/utils/is-e2-e';
import LayoutGame from '~/routes/layout-game';

/**
 * Background music between rounds is meant to be watched, so the chrome gets out of the way.
 * Shortened under e2e so a spec can watch it happen without waiting out the real ten seconds - the
 * same trick the song list's grouping threshold and the phone's language cutoff use.
 */
const CHROME_IDLE_MS = isE2E() ? 1_500 : 10_000;

/** Enough of the running order for the room to decide whether to veto with Skip. */
const UP_NEXT_COUNT = 3;

function Jukebox() {
  useBackground(false);
  useBackgroundMusic(false);
  const navigate = useSmoothNavigate();
  const goBack = () => navigate('menu/');
  const { width, height } = useViewportSize();
  const [currentlyPlaying, setCurrentlyPlaying] = useState(0);
  const songList = useSongIndex();
  const player = useRef<VideoPlayerRef>(null);

  const [shuffledList, setShuffledList] = useState<SongPreview[]>([]);

  useEffect(() => songList.data && setShuffledList(shuffle(songList.data)), [songList.data]);

  const playNext = () => songList.data && setCurrentlyPlaying((current) => (current + 1) % songList.data.length);

  const [chromeVisible, setChromeVisible] = useState(true);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read by the key handler below, which is registered once and must see the current value.
  const chromeVisibleRef = useRef(true);
  chromeVisibleRef.current = chromeVisible;

  const showChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setChromeVisible(false), CHROME_IDLE_MS);
  }, []);

  useEffect(() => {
    showChrome();

    const onInput = (event: Event) => {
      const wasHidden = !chromeVisibleRef.current;
      showChrome();
      // With the chrome hidden, the first press only brings it back. Otherwise Enter would fire the
      // default control and leave the jukebox for the song's preview, which is not what somebody
      // reaching for a key to see what is playing asked for. Capture phase and
      // `stopImmediatePropagation` because `useKeyboard` listens on the document as it bubbles.
      if (wasHidden && event.type === 'keydown') event.stopImmediatePropagation();
    };

    document.addEventListener('keydown', onInput, { capture: true });
    document.addEventListener('pointermove', onInput, { capture: true });

    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      document.removeEventListener('keydown', onInput, { capture: true });
      document.removeEventListener('pointermove', onInput, { capture: true });
    };
  }, [showChrome]);

  // Hints along the bottom rather than in the corner: the corner box is fixed top right at z-1000,
  // in the same corner the toolbar and Exit are already sharing.
  //
  // `grid` rather than the default vertical: Exit is a row of its own above Sing and Skip, which sit
  // side by side, and a vertical nav binds only up and down - Skip would be unreachable, both for a
  // singer and for `navigateWithKeyboard`, which reaches a second column by pressing Right.
  // Read before the early return below, so the subtitle can name it: the phone is the veto remote
  // on this screen, and Sing and Skip were asking about a song it could not see.
  const nowPlaying = shuffledList[currentlyPlaying];

  const { register } = useKeyboardNav({
    onBackspace: goBack,
    backspaceHelp: 'Exit jukebox',
    title: 'Jukebox',
    // Skip and the way out are what you do INSTEAD; this is what the screen is for.
    primary: 'sing-button',
    // Changes once a SONG, which is one republish per song. The elapsed time beside it on screen
    // moves twice a second and deliberately never reaches a descriptor, for the same reason.
    subtitle: nowPlaying && `${nowPlaying.title} \u00b7 ${nowPlaying.artist}`,
    direction: 'grid',
    presentation: 'strip',
  });

  const [progress, setProgress] = useState<{ current: seconds; total: seconds }>({ current: 0, total: 0 });

  useEffect(() => {
    setProgress({ current: 0, total: 0 });

    const read = async () => {
      if (!player.current) return;
      const [current, total] = await Promise.all([player.current.getCurrentTime(), player.current.getDuration()]);
      setProgress({ current, total });
    };

    void read();
    // Twice a second is smooth enough for a line this size and cheap enough to leave running all
    // evening. Deliberately NOT in any control's label - a descriptor that named the elapsed time
    // would republish the whole mirrored keyboard to every phone twice a second.
    const interval = setInterval(() => void read(), 500);

    return () => clearInterval(interval);
  }, [currentlyPlaying]);

  if (!shuffledList.length || !width || !height) return null;

  const song = shuffledList[currentlyPlaying];
  const navigateUrl = buildUrl(`game/`, { song: song.id, playlist: 'All' });
  const genre = songGenre(song.genre);
  const upNext = Array.from(
    { length: Math.min(UP_NEXT_COUNT, shuffledList.length - 1) },
    (_, index) => shuffledList[(currentlyPlaying + index + 1) % shuffledList.length],
  );
  const elapsed = formatSongLength(progress.current) ?? '0:00';
  const total = formatSongLength(progress.total);
  const playedRatio = progress.total > 0 ? Math.min(progress.current / progress.total, 1) : 0;

  return (
    <LayoutGame>
      <Helmet>
        <title>Jukebox | AllKaraoke.Party - Free Online Karaoke Party Game</title>
      </Helmet>
      <NoPrerender>
        <div
          className="relative overflow-hidden bg-black"
          style={{ width, height }}
          data-test="jukebox-container"
          data-song={song.id}
          data-chrome-visible={chromeVisible}>
          <div className="absolute inset-0">
            <VideoPlayer
              ref={player}
              autoplay
              width={width}
              height={height}
              volume={song.volume}
              video={song.video}
              startAt={song.videoGap}
              onStateChange={(state) => {
                if (state === VideoState.ENDED) playNext();
              }}
            />
          </div>

          <KeyboardNavContext value={register}>
            {/* One fade for the lot: the video is the screen and the chrome is a guest on it. Left in
                the DOM rather than unmounted, so the controls keep their mirrored descriptors and a
                phone does not lose its keyboard because the TV went quiet. */}
            <div
              className={`typography pointer-events-none absolute inset-0 transition-opacity duration-500 ${
                chromeVisible ? 'opacity-100' : 'opacity-0'
              }`}
              data-test="jukebox-chrome">
              {/* Starts below the toolbar rather than beside it: `LayoutGame` fixes help, fullscreen and
                  connect-phone into the top right corner at z-10000 on every route, so the frame's
                  own corner for Exit is already occupied. */}
              <div className="pointer-events-auto absolute inset-x-0 top-0 flex items-start justify-between gap-6 bg-gradient-to-b from-black/85 to-transparent px-10 pt-14 pb-16">
                <div className="flex flex-col gap-1">
                  <span className="stroke-text text-[2.125rem] leading-none font-bold">Jukebox</span>
                  <span className="text-lg text-white/60">
                    Whole library, shuffled ·{' '}
                    <span data-test="jukebox-catalogue-size">{groupDigits(songList.data.length)}</span> songs
                  </span>
                </div>
                <NavButton
                  name="jukebox-back"
                  size="small"
                  variant="back"
                  remoteLabel="Back to menu"
                  className="shrink-0"
                  onClick={goBack}>
                  Exit jukebox
                </NavButton>
              </div>

              <div className="pointer-events-auto absolute inset-x-0 bottom-0 flex flex-col gap-6 bg-gradient-to-t from-black/90 to-transparent px-10 pt-24 pb-20">
                <div className="flex flex-wrap items-end justify-between gap-x-10 gap-y-6">
                  <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                    <span className="text-[1.0625rem] tracking-[0.08em] text-white/45 uppercase">Now playing</span>
                    <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1.5">
                      <span className="ph-no-capture text-active stroke-text truncate text-[2.75rem] leading-none font-bold">
                        {song.title}
                      </span>
                      <span className="ph-no-capture stroke-text truncate text-[1.625rem] font-bold text-white/70">
                        {song.artist}
                      </span>
                      {/* Same chip the thumbnails carry (`Thumbnail.Genre`), drawn here rather than
                          reused: that one reads the song off the song card's own context. Labelled
                          with the spelling this song's own file carries, because one song playing on
                          its own has no neighbour to disagree with - see `useGenreLabels`. */}
                      {genre && (
                        <span
                          data-test="song-genre"
                          title={genre}
                          className="flex h-[1.875rem] max-w-[45%] items-center rounded-lg bg-black/75 px-2.5 text-[0.9375rem] font-semibold">
                          <span className="truncate tracking-[0.02em] uppercase">{genre}</span>
                        </span>
                      )}
                    </div>
                    {/* A plain line: no section ticks and no scores, because nobody is being scored. */}
                    <div className="mt-1 flex items-center gap-3.5" data-test="jukebox-progress">
                      <span className="text-[0.9375rem] text-white/55 tabular-nums">{elapsed}</span>
                      <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-white/20">
                        <div
                          className="bg-active h-full rounded-full"
                          style={{ width: `${playedRatio * 100}%` }}
                          data-test="jukebox-progress-played"
                        />
                      </div>
                      {total && <span className="text-[0.9375rem] text-white/55 tabular-nums">{total}</span>}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-3.5">
                    <NavButton
                      name="sing-button"
                      size="large"
                      className="px-14"
                      remoteIcon="play"
                      isDefault
                      onClick={() => navigate(navigateUrl)}>
                      Sing this song
                    </NavButton>
                    <NavButton name="skip-button" size="large" className="px-10" remoteIcon={null} onClick={playNext}>
                      Skip ▶▶
                    </NavButton>
                  </div>
                </div>

                {upNext.length > 0 && (
                  <div className="flex items-center gap-5" data-test="jukebox-up-next">
                    <span className="shrink-0 text-[0.9375rem] tracking-[0.08em] text-white/40 uppercase">Up next</span>
                    <div className="flex min-w-0 items-center gap-3.5">
                      {upNext.map((next) => (
                        <div key={next.id} className="flex min-w-0 items-center gap-2.5" data-song={next.id}>
                          <div
                            className="h-11 w-20 shrink-0 rounded-md bg-[#2b2b2b] bg-cover bg-center bg-no-repeat"
                            style={{ backgroundImage: `url('https://i3.ytimg.com/vi/${next.video}/hqdefault.jpg')` }}
                          />
                          <div className="flex min-w-0 flex-col">
                            <span className="ph-no-capture truncate text-[0.9375rem] font-bold text-white/85">
                              {next.title}
                            </span>
                            <span className="ph-no-capture truncate text-[0.875rem] text-white/45">{next.artist}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </KeyboardNavContext>
        </div>
      </NoPrerender>
    </LayoutGame>
  );
}

export default Jukebox;
